import React from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import { ProtectedRoute } from 'src/hoc/protectedRoute';

import { accessDataSelector } from 'src/store/auth';

import {
  Automode,
  ChaosMode,
  ChaosWidgetDashboard,
  ChaosWidgetView,
  ChaosEffectsWidgetView,
  Commands,
  Dashboard,
  Giveaway,
  GiveawayEmbed,
  GiveawayEmbedView,
  Landing,
  Privacy,
  Profile,
  Terms,
  TwitchCallback,
  Users,
  Viewers,
  DesktopOAuthAuthorize
} from 'src/features';

import { ROUTE } from 'src/constants/routes';
import { PAGE_KEYS } from 'src/constants/access';

import { AntLayout, Layout } from 'src/layouts';
import { getFirstAllowedDashboardRoute, hasPageAccess } from 'src/utils';

import 'react-toastify/dist/ReactToastify.css';
import './app.scss';

export const App = () => {
  const access = useSelector(accessDataSelector);

  const fallbackDashboardRoute = getFirstAllowedDashboardRoute(access) || ROUTE.MAIN;
  const can = (page) => hasPageAccess(access, page);

  return (
    <div className="App">
      <Routes>
        <Route path={ROUTE.EMBED.GIVEAWAY} element={<GiveawayEmbedView />} />
        <Route path="/:uuid/embed/*" element={<GiveawayEmbedView />} />
        <Route path={ROUTE.EMBED.CHAOS_WIDGET} element={<ChaosWidgetView />} />
        <Route path="/:uuid/chaos-widget/*" element={<ChaosWidgetView />} />
        <Route path="/:uuid/chaos-effects" element={<ChaosEffectsWidgetView />} />
        <Route path="/:uuid/chaos-effects/*" element={<ChaosEffectsWidgetView />} />

        <Route element={<Layout />}>
          <Route path={ROUTE.MAIN} element={<Landing />} />
          <Route path={ROUTE.TERMS} element={<Terms />} />
          <Route path={ROUTE.PRIVACY} element={<Privacy />} />
          <Route path={ROUTE.AUTH.TWITCH_CALLBACK} element={<TwitchCallback />} />
        </Route>

        <Route element={<ProtectedRoute />}>
          <Route element={<AntLayout />}>
            <Route path={ROUTE.AUTH.DESKTOP_OAUTH_AUTHORIZE} element={<DesktopOAuthAuthorize />} />
            <Route
              path={ROUTE.DASHBOARD.MAIN}
              element={
                can(PAGE_KEYS.DASHBOARD) ? (
                  <Dashboard />
                ) : (
                  <Navigate to={fallbackDashboardRoute} replace />
                )
              }
            />
            <Route
              path={ROUTE.DASHBOARD.COMMANDS}
              element={
                can(PAGE_KEYS.COMMANDS) ? (
                  <Commands />
                ) : (
                  <Navigate to={fallbackDashboardRoute} replace />
                )
              }
            />
            <Route
              path={ROUTE.DASHBOARD.AUTOMODE}
              element={
                can(PAGE_KEYS.AUTOMODE) ? (
                  <Automode />
                ) : (
                  <Navigate to={fallbackDashboardRoute} replace />
                )
              }
            />
            <Route
              path={ROUTE.DASHBOARD.CHAOS}
              element={
                can(PAGE_KEYS.CHAOS) ? (
                  <ChaosMode />
                ) : (
                  <Navigate to={fallbackDashboardRoute} replace />
                )
              }
            />
            <Route
              path={ROUTE.DASHBOARD.CHAOS_WIDGET}
              element={
                can(PAGE_KEYS.CHAOS_WIDGET) ? (
                  <ChaosWidgetDashboard />
                ) : (
                  <Navigate to={fallbackDashboardRoute} replace />
                )
              }
            />
            <Route
              path={ROUTE.DASHBOARD.GIVEAWAY}
              element={
                can(PAGE_KEYS.GIVEAWAY) ? (
                  <Giveaway />
                ) : (
                  <Navigate to={fallbackDashboardRoute} replace />
                )
              }
            />
            <Route
              path={ROUTE.DASHBOARD.GIVEAWAY_EMBED}
              element={
                can(PAGE_KEYS.GIVEAWAY_EMBED) ? (
                  <GiveawayEmbed />
                ) : (
                  <Navigate to={fallbackDashboardRoute} replace />
                )
              }
            />
            <Route
              path={ROUTE.DASHBOARD.VIEWERS}
              element={
                can(PAGE_KEYS.VIEWERS) ? (
                  <Viewers />
                ) : (
                  <Navigate to={fallbackDashboardRoute} replace />
                )
              }
            />
            <Route
              path={ROUTE.DASHBOARD.PROFILE}
              element={
                can(PAGE_KEYS.PROFILE) ? (
                  <Profile />
                ) : (
                  <Navigate to={fallbackDashboardRoute} replace />
                )
              }
            />
            <Route
              path={ROUTE.DASHBOARD.USERS}
              element={
                can(PAGE_KEYS.USERS) ? <Users /> : <Navigate to={fallbackDashboardRoute} replace />
              }
            />
          </Route>
        </Route>

        <Route path="*" element={<Navigate to={ROUTE.MAIN} />} />
      </Routes>

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};
